/**
 * Milestone fan-out (gap G26).
 *
 * A real achievement currently reaches exactly one person. When an operator
 * passes their 1,000th wash, the only thing that happens is confetti on the
 * tablet in front of whoever happened to be at the till. The agent who signed
 * that operator — whose commission was paid once, months ago, and who has had
 * no reason to think about them since — never hears. Neither does the customer
 * whose wash was the one that crossed the line.
 *
 * That is a waste of the rarest thing a business gets: a moment where somebody
 * is already feeling good. ADR-0029's alignment problem is that an agent's
 * incentive ends at the signup; this is the emotional half of the answer.
 *
 * ## The same event is a different story to each person
 *
 * The model here is not "send the milestone to three people". A milestone is a
 * fact; what each party should be TOLD about it differs:
 *
 *   operator — "You have washed 1,000 cars." Their achievement, plainly.
 *   agent    — "A carwash you brought to the network passed 1,000 washes."
 *              Framed around their contribution, because that is the part that
 *              keeps them invested.
 *   customer — "Yours was their 1,000th wash." A small, specific delight that
 *              says nothing about the business's numbers beyond the milestone
 *              itself.
 *
 * ## What is deliberately withheld
 *
 * Wash volume is competitively sensitive, and an agent is an independent
 * contractor, not staff. So the agent's message carries the MILESTONE and
 * nothing else — no daily rate, no revenue, no trend — and the operator can
 * turn the sharing off entirely (`shareMilestonesWithAgent`). The operator is
 * told in their own message that their agent was told, because sharing someone
 * else's business data without saying so is how a nice feature becomes a
 * betrayal.
 *
 * The customer's message names no other customer and no totals beyond the
 * milestone number they themselves triggered.
 *
 * Pure and total: no I/O, no clock, no randomness. Copy is returned as i18n
 * KEYS, never resolved strings — this runs in Functions where there is no i18n
 * runtime, and each recipient reads in their own locale.
 */

/**
 * Wash counts worth stopping for.
 *
 * Deliberately sparse and widening. A milestone every ten washes is not a
 * milestone, it is noise, and the fastest way to make people mute a channel is
 * to celebrate something unremarkable.
 */
export const WASH_MILESTONES: readonly number[] = [1, 10, 100, 500, 1_000, 5_000, 10_000];

/** Who a fanned-out message is for. */
export type MilestoneAudience = 'operator' | 'agent' | 'customer';

/** One recipient's version of a milestone. */
export interface MilestoneMessage {
  readonly audience: MilestoneAudience;
  /**
   * Who this goes to: the tenant, agent or customer id, depending on audience.
   *
   * Explicit rather than derived. The first version of the writer recovered the
   * agent id by splitting the dedupe key, which works right up until the key
   * format changes and messages start landing in a document called "unknown".
   */
  readonly recipientId: string;
  /** i18n key for the headline, resolved by the receiving app. */
  readonly titleKey: string;
  /** i18n key for the body. */
  readonly bodyKey: string;
  /** Interpolation values for those keys. */
  readonly vars: Readonly<Record<string, string | number>>;
  /**
   * Stable identity for this exact message, so a retried trigger cannot
   * celebrate the same milestone twice. Firestore triggers are at-least-once;
   * a duplicate congratulation is worse than none, because it reveals the
   * machinery behind what should feel personal.
   */
  readonly dedupeKey: string;
}

/** The achievement being announced. */
export interface WashMilestoneEvent {
  readonly tenantId: string;
  /** Operator's display name, for the agent's and customer's copy. */
  readonly tenantName: string;
  /** The threshold that was crossed. */
  readonly milestone: number;
  /** Agent who signed this operator, when there is one. */
  readonly agentId?: string;
  /** Customer whose wash crossed the line, when known. */
  readonly customerUid?: string;
}

/** What the operator has chosen to share. */
export interface MilestoneSharingPolicy {
  /**
   * Whether the agent who signed this operator hears about milestones.
   *
   * Defaults ON at the call site, and the operator's own message says so — an
   * agent is an existing commercial partner who already sees subscription
   * status, and a channel that never reports back is the alignment problem this
   * feature exists to fix. But it stays the operator's call, and turning it off
   * must be one tap.
   */
  readonly shareWithAgent: boolean;
  /**
   * Whether the customer who triggered it is told. Separate from the agent
   * switch: it shares strictly less (one number the customer caused) and an
   * operator may reasonably want one and not the other.
   */
  readonly shareWithCustomer: boolean;
}

/**
 * Which milestones a counter crossed moving from `before` to `after`.
 *
 * Returns every threshold in the interval, not just the highest. A backfill or
 * a batch import can jump several at once, and silently dropping the ones in
 * between would mean an operator never learns they passed 100 because they
 * passed 500 in the same write.
 *
 * Exclusive of `before`, inclusive of `after`: crossing TO the number is the
 * event.
 */
export function crossedMilestones(before: number, after: number): number[] {
  const from = Number.isFinite(before) ? before : 0;
  const to = Number.isFinite(after) ? after : 0;
  if (to <= from) return [];
  return WASH_MILESTONES.filter((m) => m > from && m <= to);
}

/**
 * Turn one milestone into the messages it should produce.
 *
 * Returns an empty array rather than throwing when there is nobody to tell —
 * an operator with no agent and an unidentified walk-in customer is an ordinary
 * case, not an error.
 */
export function fanOutMilestone(
  event: WashMilestoneEvent,
  policy: MilestoneSharingPolicy,
): MilestoneMessage[] {
  if (!event.tenantId || !Number.isFinite(event.milestone) || event.milestone <= 0) return [];

  const base = `${event.tenantId}:washes:${event.milestone}`;
  const messages: MilestoneMessage[] = [];

  const agentWillHear = policy.shareWithAgent && !!event.agentId;

  // The operator always hears about their own achievement.
  messages.push({
    audience: 'operator',
    titleKey: 'milestone.washes.operator.title',
    // Two bodies, because the operator must know when their agent was told.
    // Sharing a business's numbers without saying so is the version of this
    // feature that costs trust instead of building it.
    bodyKey: agentWillHear
      ? 'milestone.washes.operator.bodyShared'
      : 'milestone.washes.operator.body',
    recipientId: event.tenantId,
    vars: { count: event.milestone },
    dedupeKey: `${base}:operator`,
  });

  if (agentWillHear && event.agentId) {
    messages.push({
      audience: 'agent',
      titleKey: 'milestone.washes.agent.title',
      bodyKey: 'milestone.washes.agent.body',
      recipientId: event.agentId,
      // The milestone and the name — nothing about rate, revenue or trend.
      vars: { count: event.milestone, tenant: event.tenantName },
      dedupeKey: `${base}:agent:${event.agentId}`,
    });
  }

  if (policy.shareWithCustomer && event.customerUid) {
    messages.push({
      audience: 'customer',
      titleKey: 'milestone.washes.customer.title',
      bodyKey: 'milestone.washes.customer.body',
      recipientId: event.customerUid,
      vars: { count: event.milestone, tenant: event.tenantName },
      dedupeKey: `${base}:customer:${event.customerUid}`,
    });
  }

  return messages;
}
